import { useSetAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
	Button,
	Group,
	Section,
	SteppedProgressBar,
	TitleGroup,
	toast,
} from '@ekhodealer/ekho-common/rev';

import { useGetBankAccounts } from '@common/api/plaid/plaidService';
import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';
import { useUpdateOnboardingPage } from '@features/account-management/api/configuration/useUpdateOnboardingPage';
import {
	forceValidateBankAccountDetailsAtom,
	forceValidateBusinessAddressesAtom,
	forceValidateBusinessContactAtom,
	forceValidateBusinessIdentityAtom,
	forceValidateDmsConnectionAtom,
} from '@features/account-management/atoms/onboardingValidationAtoms';
import {
	AccessPage,
	BankAccountPage,
	BusinessDetailsPage,
	CompletionPage,
	HowEkhoWorksPage,
	InventoryConnectionPage,
	PricingValuePage,
} from '@features/account-management/components/onboarding-pages';
import {
	headerContent,
	onboardingContent,
	OnboardingPage,
} from '@features/account-management/components/onboarding-pages/content';
import {
	getEarliestIncompletePage,
	hasCompletedMinimumOnboarding,
	ONBOARDING_GROUP,
	type OnboardingGroup,
} from '@features/account-management/lib/hasCompletedMinimumOnboarding';
import { paths } from '@config/paths';

import './index.scss';

function ServicesProviderOnboarding() {
	const navigate = useNavigate();
	const { data: servicesProvider } = useCurrentServicesProvider();
	const { data: bankAccounts = [] } = useGetBankAccounts(servicesProvider?.id ?? '');

	const { mutateAsync: updateOnboardingPage, isPending: isUpdatingPage } =
		useUpdateOnboardingPage();

	// Force-validation atom setters
	const setForceValidateDmsConnection = useSetAtom(forceValidateDmsConnectionAtom);
	const setForceValidateBusinessIdentity = useSetAtom(forceValidateBusinessIdentityAtom);
	const setForceValidateBusinessAddresses = useSetAtom(forceValidateBusinessAddressesAtom);
	const setForceValidateBusinessContact = useSetAtom(forceValidateBusinessContactAtom);
	const setForceValidateBankAccountDetails = useSetAtom(forceValidateBankAccountDetailsAtom);

	// Track which operation is in progress ('back' | 'next' | null)
	const [pendingOperation, setPendingOperation] = useState<'back' | 'next' | null>(null);

	// Get current page from services provider (source of truth)
	const currentPage = servicesProvider?.onboardingPage || 1;

	// Calculate derived values from current page
	const totalPages = useMemo(() => Object.keys(onboardingContent).length, []);
	const isFirstPage = currentPage === 1;
	const isLastPage = currentPage === totalPages;

	/** Set forceValidation atoms for all incomplete groups. */
	const activateForceValidation = (incompleteGroups: OnboardingGroup[]): void => {
		const groupSet = new Set(incompleteGroups);

		setForceValidateDmsConnection(groupSet.has(ONBOARDING_GROUP.INVENTORY_CONNECTION));
		setForceValidateBusinessIdentity(groupSet.has(ONBOARDING_GROUP.BUSINESS_IDENTITY));
		// businessIdentity covers both identity and addresses forms
		setForceValidateBusinessAddresses(groupSet.has(ONBOARDING_GROUP.BUSINESS_IDENTITY));
		setForceValidateBusinessContact(groupSet.has(ONBOARDING_GROUP.PRIMARY_CONTACT));
		setForceValidateBankAccountDetails(groupSet.has(ONBOARDING_GROUP.BANK_ACCOUNT_DETAILS));
	};

	const handleBack = async () => {
		if (!isFirstPage && servicesProvider?.id && !isUpdatingPage) {
			const previousPage = currentPage - 1;
			setPendingOperation('back');
			try {
				await updateOnboardingPage({
					page: previousPage,
				});
			} catch (error) {
				console.error('Failed to save onboarding progress:', error);
				// Don't navigate if saving fails
			} finally {
				setPendingOperation(null);
			}
		}
	};

	const handleNext = async () => {
		if (servicesProvider?.id && !isUpdatingPage) {
			// Validate minimum onboarding requirements before leaving the bank account page
			if (currentPage === OnboardingPage.BankAccount) {
				const validation = hasCompletedMinimumOnboarding(servicesProvider, bankAccounts);
				if (!validation.isComplete) {
					activateForceValidation(validation.incompleteGroups);

					const earliestPage = getEarliestIncompletePage(validation.incompleteGroups);
					if (earliestPage !== null && earliestPage !== currentPage) {
						setPendingOperation('next');
						try {
							await updateOnboardingPage({ page: earliestPage });
						} catch (error) {
							console.error('Failed to navigate to incomplete page:', error);
						} finally {
							setPendingOperation(null);
						}
					}

					toast({
						message: validation.message ?? '',
						variant: 'error',
					});
					return;
				}
			}

			const page = isLastPage ? currentPage : currentPage + 1;
			const onboardingCompleted = isLastPage;
			setPendingOperation('next');
			try {
				await updateOnboardingPage({
					page,
					onboardingCompleted,
				});
				if (isLastPage) {
					navigate(paths.orders.getHref(servicesProvider.id), { replace: true });
				}
			} catch (error) {
				console.error('Failed to save onboarding progress:', error);
				// Don't navigate if saving fails
			} finally {
				setPendingOperation(null);
			}
		}
	};

	// Render the appropriate page component based on current page
	const renderPageContent = () => {
		switch (currentPage) {
			case 1:
				return <HowEkhoWorksPage />;
			case 2:
				return <PricingValuePage />;
			case 3:
				return <InventoryConnectionPage />;
			case 4:
				return <BusinessDetailsPage />;
			case 5:
				return <BankAccountPage />;
			case 6:
				return <AccessPage />;
			case 7:
				return <CompletionPage />;
			default:
				return <HowEkhoWorksPage />;
		}
	};

	const ekhoLogo = (
		<a href='https://ekho.com' target='_blank' rel='noopener noreferrer'>
			<img
				src='https://cdn.builtin.com/cdn-cgi/image/f=auto,fit=contain,w=200,h=200,q=100/sites/www.builtin.com/files/2025-07/ekho_dealer_logo.jpeg'
				alt='Ekho Logo'
				className='dealer-icon-image'
			/>
		</a>
	);

	if (!servicesProvider) {
		return null;
	}

	return (
		<div className='dealer-onboarding-container'>
			{/* Header */}
			<Section variant='transparent' layout='padded'>
				<header>
					<TitleGroup
						title={
							<Group itemsSpacing='10' itemsAlignY='center'>
								<Group.Item>{ekhoLogo}</Group.Item>
								<Group.Item>{headerContent.title}</Group.Item>
							</Group>
						}
						description={headerContent.subtitle.replace(
							'{name}',
							servicesProvider.name || '',
						)}
						level='section'
					/>
				</header>
			</Section>

			<Section variant='solid' layout='padded'>
				<SteppedProgressBar
					steps={Object.values(onboardingContent).map(
						(content) => content.navigationTitle,
					)}
					currentStepIndex={currentPage - 1} // Convert from 1-based to 0-based index
					showLabels={true}
				/>
			</Section>

			{/* Main Content */}
			<div className='dealer-onboarding-main'>
				<Section variant='transparent' layout='padded'>
					<main>{renderPageContent()}</main>
				</Section>
			</div>

			{/* Footer Navigation */}
			<Section variant='solid' layout='padded' className='dealer-onboarding-footer-section'>
				<footer className='dealer-onboarding-footer'>
					{!isLastPage && (
						<Button
							size='large'
							onPress={handleBack}
							variant='outline'
							isDisabled={isFirstPage || isUpdatingPage}
							isLoading={isUpdatingPage && pendingOperation === 'back'}>
							Back
						</Button>
					)}
					<Button
						size='large'
						onPress={handleNext}
						isDisabled={isUpdatingPage}
						isLoading={isUpdatingPage && pendingOperation === 'next'}>
						{isLastPage ? 'Finish' : 'Continue'}
					</Button>
				</footer>
			</Section>
		</div>
	);
}

export default ServicesProviderOnboarding;
